import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Activity, AlertTriangle, ArrowDownRight, ArrowUpRight,
  Building2, Calendar, CheckCircle2, ChevronRight, Send,
  TrendingDown, TrendingUp, Users, Clock, Printer, Sparkles,
} from "lucide-react";
import ComplianceCoverage from "@/pages/ComplianceCoverage";

const SCARLET = "#ff4500";
const NAVY = "#1a1a1a";
const GREEN = "#1a8754";
const AMBER = "#d6a900";

const PERIODS = [
  { k: "7d", label: "7 days" },
  { k: "30d", label: "30 days" },
  { k: "90d", label: "90 days" },
  { k: "all", label: "All time" },
];

/** Compute a 0-100 health score for a hub. Same shape whichever period the caller filters by. */
function healthScore(p) {
  const openTotal = p.open_now_total ?? 0;
  const overdue = p.overdue_total ?? 0;
  const completed = p.completed_last_week ?? 0;
  const activeDenom = Math.max(openTotal + completed, 1);
  const overduePenalty = Math.min((overdue / Math.max(openTotal, 1)) * 55, 55);
  // Reward completion volume (up to +15)
  const completionBonus = Math.min(completed * 3, 15);
  // Base of 85, subtract overdue penalty, add completion bonus, capped
  const raw = 85 - overduePenalty + completionBonus - (p.overdue_delta > 0 ? Math.min(p.overdue_delta * 4, 20) : 0);
  return Math.max(0, Math.min(100, Math.round(raw)));
}
function healthBand(score) {
  if (score >= 75) return { color: GREEN, label: "Healthy", emoji: "●" };
  if (score >= 45) return { color: AMBER, label: "Watch", emoji: "◐" };
  return { color: SCARLET, label: "Critical", emoji: "▲" };
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

async function loadAll(period, templateIds) {
  const params = { period };
  if (templateIds && templateIds.length) params.template_ids = templateIds.join(",");
  const [p, h] = await Promise.all([
    api.get("/me/pulses", { params }),
    api.get("/pulses/history"),
  ]);
  return { data: p.data, history: h.data || [] };
}

export default function HubPulse() {
  const [state, setState] = useState({ data: null, history: [], errorMsg: "", sending: false });
  const [period, setPeriod] = useState("7d");
  const [showTemplateFilter, setShowTemplateFilter] = useState(false);
  const [templateFilter, setTemplateFilter] = useState(new Set());
  const [templates, setTemplates] = useState([]);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const reload = async () => {
    try {
      const { data, history } = await loadAll(period, Array.from(templateFilter));
      if (mounted.current) setState((s) => ({ ...s, data, history, errorMsg: "" }));
    } catch (e) {
      if (mounted.current) setState((s) => ({ ...s, errorMsg: e?.response?.data?.detail || "Failed to load pulse" }));
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [period, templateFilter]);
  useEffect(() => {
    api.get("/checklist-templates").then((r) => setTemplates(r.data || [])).catch(() => {});
  }, []);

  const dispatchNow = async () => {
    setState((s) => ({ ...s, sending: true }));
    try {
      const resp = await api.post("/pulses/dispatch", {});
      toast.success(`Pulse dispatched · ${resp.data?.run?.recipient_count ?? 0} email(s) queued`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dispatch failed");
    }
    if (mounted.current) setState((s) => ({ ...s, sending: false }));
  };

  const exportPdf = () => {
    // Browser-native print keeps things simple; the page's CSS is already print-friendly.
    window.print();
  };

  const { data, history, errorMsg, sending } = state;

  const orgScore = useMemo(() => {
    if (!data?.pulses?.length) return null;
    const scores = data.pulses.map(healthScore);
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  }, [data]);
  const orgBand = orgScore != null ? healthBand(orgScore) : null;

  if (errorMsg) return (
    <div className="p-8">
      <div className="bg-[#fff1ea] border border-[#ff4500]/30 rounded-2xl p-6 max-w-xl" data-testid="pulse-error">
        <div className="font-display font-black text-lg">{errorMsg}</div>
        <div className="text-sm text-[#1a1a1a]/70 mt-1">Only Admins (of a hub) and Controllers (of the organization) can view CMD Health.</div>
      </div>
    </div>
  );

  if (!data) return <div className="p-10 font-mono text-[#1a1a1a]/55 text-[11px] uppercase tracking-wider">Loading CMD Health…</div>;

  const r = data.rollup || {};
  const pulses = data.pulses || [];
  const lastRun = data.last_run;

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="cmd-health-page">
      <section className="hero-navy rounded-3xl p-6 lg:p-8 text-white overflow-hidden relative">
        <div className="absolute inset-0 dotted opacity-25" />
        <div className="absolute -top-20 -right-10 w-72 h-72 rounded-full bg-[#ff4500]/25 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
              <Activity className="w-3 h-3 text-[#ff5f15]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em]">CMD Health · quick-glance ops pulse</span>
            </div>
            <h1 className="mt-3 font-display font-black text-3xl lg:text-4xl tracking-tight" data-testid="pulse-heading">
              CMD <span className="text-[#ff5f15]">Health.</span>
            </h1>
            <p className="mt-2 text-sm text-white/75 max-w-xl">
              One-look health check across every hub. Green = healthy, amber = needs a nudge, red = act now. Weekly digest auto-dispatches every Monday.
            </p>
            {lastRun && (
              <div className="mt-2 text-[11px] font-mono uppercase tracking-wider text-white/55" data-testid="pulse-last-run">
                Last dispatch · {fmtDate(lastRun.dispatched_at)} · wk {lastRun.iso_week}/{lastRun.iso_year} · {lastRun.recipient_count} recipient{lastRun.recipient_count === 1 ? "" : "s"} · {lastRun.trigger}
              </div>
            )}
          </div>
          {orgBand && (
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/15 flex items-center gap-4" data-testid="cmd-health-org-score">
              <div className="w-20 h-20 rounded-full flex items-center justify-center border-4" style={{ borderColor: orgBand.color }}>
                <div className="font-display font-black text-3xl" style={{ color: orgBand.color }}>{orgScore}</div>
              </div>
              <div>
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/60">// Org score</div>
                <div className="font-display font-black text-xl" style={{ color: orgBand.color }}>{orgBand.label}</div>
                <div className="text-[11px] text-white/60 mt-0.5">Avg across {pulses.length} hub{pulses.length === 1 ? "" : "s"}</div>
              </div>
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div className="relative mt-5 flex items-center gap-2 flex-wrap">
          <div className="flex bg-white/10 border border-white/15 rounded-full p-1">
            {PERIODS.map((p) => (
              <button key={p.k} data-testid={`period-${p.k}`} onClick={() => setPeriod(p.k)}
                className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-wider font-bold ${period === p.k ? "bg-[#ff4500] text-white" : "text-white/60 hover:text-white"}`}>
                {p.label}
              </button>
            ))}
          </div>
          <button data-testid="toggle-tpl-filter" onClick={() => setShowTemplateFilter((v) => !v)}
            className="text-[11px] uppercase tracking-wider font-bold text-white/70 hover:text-white bg-white/10 border border-white/15 rounded-full px-3 py-1.5">
            Filter audits {templateFilter.size ? `· ${templateFilter.size}` : ""}
          </button>
          <button data-testid="pulse-dispatch-now" onClick={dispatchNow} disabled={sending}
            className="btn-pill bg-[#ff4500] text-white hover:bg-[#cc3500] font-display font-black shadow-glow disabled:opacity-50 text-[11px]">
            <Send className="w-3 h-3" /> {sending ? "Sending…" : "Send pulse"}
          </button>
          <button data-testid="pulse-export-pdf" onClick={exportPdf}
            className="btn-pill bg-white/10 border border-white/25 text-white hover:bg-white/20 text-[11px]">
            <Printer className="w-3 h-3" /> Print / PDF
          </button>
        </div>
        {showTemplateFilter && (
          <div className="relative mt-3 bg-white/5 border border-white/15 rounded-xl p-3 max-h-40 overflow-auto scrollbar-thin" data-testid="tpl-filter-panel">
            {templates.length === 0 && <div className="text-[11px] text-white/55">No templates.</div>}
            {templates.map((t) => {
              const on = templateFilter.has(t.id);
              return (
                <label key={t.id} className="flex items-center gap-2 py-1 text-[12px] text-white/80 cursor-pointer">
                  <input type="checkbox" checked={on} onChange={() => {
                    const n = new Set(templateFilter);
                    if (n.has(t.id)) n.delete(t.id); else n.add(t.id);
                    setTemplateFilter(n);
                  }} className="accent-[#ff4500]" />
                  {t.name}
                </label>
              );
            })}
          </div>
        )}
      </section>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="pulse-rollup">
        <RollupKpi testId="rollup-hubs" icon={Building2} label="Hubs" value={r.hubs} />
        <RollupKpi testId="rollup-members" icon={Users} label="People" value={r.members} />
        <RollupKpi testId="rollup-overdue" icon={AlertTriangle} label="Overdue" value={r.overdue_total} tone={SCARLET} accent />
        <RollupKpi testId="rollup-delta" icon={r.overdue_delta >= 0 ? TrendingUp : TrendingDown}
          label={`Δ ${period}`}
          value={(r.overdue_delta ?? 0) > 0 ? `+${r.overdue_delta}` : (r.overdue_delta ?? 0)}
          tone={r.overdue_delta > 0 ? SCARLET : GREEN} />
        <RollupKpi testId="rollup-completed" icon={CheckCircle2} label={`Done ${period}`} value={r.completed_last_week} tone={GREEN} />
      </div>

      {pulses.length === 0 ? (
        <div className="surface-card rounded-2xl p-8 text-center" data-testid="pulse-empty">
          <Activity className="w-8 h-8 text-[#1a1a1a]/25 mx-auto" />
          <div className="mt-3 font-display font-bold">No hubs in scope yet.</div>
          <div className="text-[12px] text-[#1a1a1a]/65 mt-1">Create or join a Hub to start receiving the weekly CMD Health digest. Controllers see every hub in the organization.</div>
          <Link to="/app/teams" data-testid="pulse-go-hubs" className="btn-pill btn-pill-primary mt-4">Open Hubs <ChevronRight className="w-3.5 h-3.5" /></Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {pulses.map((p) => <HubHealthCard key={p.hub.id} p={p} />)}
        </div>
      )}

      {/* Trends section — absorbed from old /app/reports */}
      <div className="surface-card rounded-2xl p-5" data-testid="trends-embedded">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-[#ff4500]" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Trends</div>
            <div className="font-display font-bold text-base">Longer-horizon patterns</div>
          </div>
          <Link to="/app/reports" data-testid="open-trends" className="ml-auto text-[11px] uppercase tracking-wider font-bold text-[#1a1a1a]/60 hover:text-[#ff4500] inline-flex items-center gap-1">
            Deep dive <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <TrendMini label="Total actions created" value={r.actions_total ?? "—"} />
          <TrendMini label="Effectiveness verified" value={r.effectiveness_verified ?? "—"} />
          <TrendMini label="Audits closed (all)" value={r.audits_total ?? "—"} />
          <TrendMini label="Avg cycle time" value={r.avg_cycle_days ? `${r.avg_cycle_days}d` : "—"} />
        </div>
      </div>

      {history.length > 0 && (
        <div className="surface-card rounded-2xl p-5" data-testid="pulse-history">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-1">// Dispatch history</div>
          <div className="font-display font-bold text-base mb-3">Last {Math.min(history.length, 12)} pulse run{history.length === 1 ? "" : "s"}</div>
          <ul className="divide-y divide-[#1a1a1a]/08">
            {history.slice(0, 12).map((h) => (
              <li key={h.id} className="py-2 flex items-center gap-3 text-[12px]" data-testid={`pulse-run-${h.id}`}>
                <Calendar className="w-3.5 h-3.5 text-[#ff4500]" />
                <div className="font-mono uppercase tracking-wider text-[10px] text-[#1a1a1a]/65 w-32">{fmtDate(h.dispatched_at)}</div>
                <div className="font-mono text-[10px] text-[#1a1a1a]/55 w-20">wk {h.iso_week}/{h.iso_year}</div>
                <div className="font-display font-bold text-sm">{h.hub_count} hub{h.hub_count === 1 ? "" : "s"} · {h.recipient_count} email{h.recipient_count === 1 ? "" : "s"}</div>
                <span className="ml-auto px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded-full bg-[#f7f5f2] text-[#1a1a1a]/70">{h.trigger}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* iter34 — Compliance Coverage embedded inside CMD Health */}
      <section className="surface-card rounded-2xl p-5" data-testid="cmd-health-coverage">
        <ComplianceCoverage embedded={true} />
      </section>
    </div>
  );
}

function TrendMini({ label, value }) {
  return (
    <div className="bg-[#f7f5f2] rounded-xl p-3">
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#1a1a1a]/55">{label}</div>
      <div className="font-display font-black text-2xl mt-1">{value}</div>
    </div>
  );
}

function HubHealthCard({ p }) {
  const score = healthScore(p);
  const band = healthBand(score);
  const delta = p.overdue_delta ?? 0;
  const deltaColor = delta > 0 ? SCARLET : delta < 0 ? GREEN : NAVY;
  const DeltaIcon = delta > 0 ? ArrowUpRight : delta < 0 ? ArrowDownRight : Clock;
  const rate = p.completion_rate;
  return (
    <Link to="/app/teams" data-testid={`cmd-hub-card-${p.hub.id}`}
      className="block surface-card rounded-2xl p-5 hover:shadow-lift hover:-translate-y-0.5 transition-all duration-150 relative overflow-hidden">
      {/* Traffic-light band */}
      <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: band.color }} />
      <div className="flex items-start gap-4">
        {/* Giant health score */}
        <div className="flex-shrink-0 w-20 h-20 rounded-2xl flex flex-col items-center justify-center border-2" style={{ borderColor: band.color, background: `${band.color}12` }}>
          <div className="font-display font-black text-3xl leading-none" style={{ color: band.color }}>{score}</div>
          <div className="font-mono text-[8px] uppercase tracking-[0.2em] mt-0.5" style={{ color: band.color }}>{band.label}</div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// {p.hub.code || "HUB"}</div>
          <div className="font-display font-black text-lg truncate">{p.hub.name}</div>
          <div className="text-[11px] text-[#1a1a1a]/65 truncate">
            {p.hub.owner || "—"} · {p.members_count} member{p.members_count === 1 ? "" : "s"}
          </div>
          <div className="mt-1.5 flex items-center gap-1.5 text-[11px]" style={{ color: deltaColor }}>
            <DeltaIcon className="w-3 h-3" />
            <span className="font-display font-bold">{delta > 0 ? `+${delta}` : delta}</span>
            <span className="font-mono text-[9px] uppercase tracking-wider text-[#1a1a1a]/50">vs last week</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-4">
        <MiniStat label="Overdue" value={p.overdue_total} tone={p.overdue_total > 0 ? SCARLET : NAVY} />
        <MiniStat label="Done 7d" value={p.completed_last_week} tone={GREEN} />
        <MiniStat label="Open" value={p.open_now_total} tone={NAVY} />
      </div>

      <div className="mt-3 bg-[#f7f5f2] rounded-xl p-2.5">
        <div className="font-mono text-[9px] uppercase tracking-wider text-[#1a1a1a]/55 mb-1">Completion rate · 7d</div>
        <div className="font-display font-bold text-base">{rate == null ? "—" : `${Math.round(rate * 100)}%`}</div>
        <div className="h-1.5 bg-white rounded-full overflow-hidden mt-1.5">
          <div className="h-full transition-all duration-500" style={{ width: `${(rate ?? 0) * 100}%`, background: GREEN }} />
        </div>
      </div>

      {(p.at_risk || []).length > 0 && (
        <div className="mt-3 text-[11px]">
          <span className="font-mono uppercase tracking-wider text-[#1a1a1a]/55">Top at-risk · </span>
          <span className="font-display font-bold text-[#ff4500]">{(p.at_risk || []).slice(0, 2).map(r => r.name).join(" · ")}</span>
        </div>
      )}
    </Link>
  );
}

function RollupKpi({ icon: Icon, label, value, accent, tone, testId }) {
  return (
    <div data-testid={testId} className={`surface-card rounded-2xl p-4 ${accent ? "ring-2 ring-[#ff4500]/30" : ""}`}>
      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#ff4500] flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
      <div className="font-display font-black text-3xl mt-1" style={{ color: tone || NAVY }}>{value ?? "—"}</div>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  return (
    <div className="bg-[#f7f5f2] rounded-lg p-2 text-center">
      <div className="font-display font-black text-lg" style={{ color: tone }}>{value ?? "—"}</div>
      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#1a1a1a]/55 mt-0.5">{label}</div>
    </div>
  );
}
